
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DividerModule } from 'primeng/divider';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { NotificationService } from '../../../../../services/shared/notification.service';

export interface BrandingSettings {
  logo?: string | null; // Base64 encoded SVG or URL
  smallLogo?: string | null; // Base64 encoded SVG or URL
  themeColors?: {
    primaryColor?: string;
  };
  // Handle nested structure from backend
  content?: BrandingSettings;
}

@Component({
  selector: 'cognos-license-details-branding',
  imports: [
    FormsModule,
    ButtonModule,
    ColorPickerModule,
    FileUploadModule,
    DividerModule
],
  templateUrl: './license-details-branding.component.html',
  styleUrl: './license-details-branding.component.scss',
})
export class LicenseDetailsBrandingComponent implements OnDestroy {
  @Input() brandingSettings: BrandingSettings | undefined = undefined;
  @Input() licenseId: string = '';
  @Output() settingsUpdated = new EventEmitter<BrandingSettings>();

  private _internalBrandingSettings: BrandingSettings = {};
  private _debounceTimer: any = null;
  private readonly DEBOUNCE_DELAY = 500; // 500ms delay
  private readonly LOGO_TYPES = ['logo', 'smallLogo'] as const;
  private readonly LOGO_LABELS: Record<(typeof this.LOGO_TYPES)[number], string> = {
    logo: 'Logo',
    smallLogo: 'Small logo',
  };

  public get currentBrandingSettings(): BrandingSettings {
    const brandingContent = this.brandingSettings?.content || this.brandingSettings;
    return {
      ...brandingContent,
      ...this._internalBrandingSettings,
    } as BrandingSettings;
  }

  public isUploading: boolean = false;
  public isSmallLogoUploading: boolean = false;
  public isSaving: boolean = false;
  public colorLabels = {
    primaryColor: 'Primary Color',
  };

  public colorDescriptions = {
    primaryColor: 'Main brand color for buttons, links, and interactive elements',
  };

  constructor(private _notificationService: NotificationService) {}

  onFileSelect(event: FileUploadHandlerEvent) {
    this.handleLogoFileSelect(event, 'logo');
  }

  onSmallLogoSelect(event: FileUploadHandlerEvent) {
    this.handleLogoFileSelect(event, 'smallLogo');
  }

  private handleLogoFileSelect(
    event: FileUploadHandlerEvent,
    logoType: (typeof this.LOGO_TYPES)[number]
  ) {
    const file = event.files[0];
    if (!file) return;

    // Validate file type
    if (!this.isValidSVG(file)) {
      this._notificationService.showErrorNotification('Please upload only SVG files');
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      this._notificationService.showErrorNotification(`${this.LOGO_LABELS[logoType]} file must be smaller than 1MB`);
      return;
    }

    this.setLogoUploadingState(logoType, true);
    this.convertToBase64(file, logoType);
  }

  private isValidSVG(file: File): boolean {
    // Check file extension
    const extension = file.name.toLowerCase().split('.').pop();
    if (extension !== 'svg') {
      return false;
    }

    // Check MIME type (be more flexible)
    const validMimeTypes = [
      'image/svg+xml',
      'image/svg',
      'application/xml',
      'text/xml',
      'application/svg+xml',
    ];
    
    // Allow empty MIME type or valid types
    return file.type === '' || validMimeTypes.includes(file.type.toLowerCase());
  }

  private convertToBase64(file: File, logoType: (typeof this.LOGO_TYPES)[number]) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        // Validate that it's actually an SVG by checking the content
        if (this.isValidSVGContent(result)) {
          this._internalBrandingSettings[logoType] = result;
          this.updateSettings();
          this._notificationService.showSuccessNotification(`${this.LOGO_LABELS[logoType]} has been successfully uploaded`);
        } else {
          // Debug: Log the content to help identify the issue
          console.error('SVG validation failed. File type:', file.type, 'File name:', file.name);
          console.error('Content preview:', result.substring(0, 200) + '...');
          this._notificationService.showErrorNotification('The uploaded file is not a valid SVG. Please check the console for details.');
        }
      }
      this.setLogoUploadingState(logoType, false);
    };
    reader.onerror = () => {
      this._notificationService.showErrorNotification('Failed to read the file');
      this.setLogoUploadingState(logoType, false);
    };
    reader.readAsDataURL(file);
  }

  private isValidSVGContent(content: string): boolean {
    try {
      // If it's a data URL, extract the base64 part and decode it
      if (content.startsWith('data:image/svg+xml;base64,')) {
        const base64Data = content.split(',')[1];
        const decodedContent = atob(base64Data);
        const lowerContent = decodedContent.toLowerCase();
        return lowerContent.includes('<svg') && lowerContent.includes('</svg>');
      } else {
        // If it's not a data URL, check directly
        const lowerContent = content.toLowerCase();
        return lowerContent.includes('<svg') && lowerContent.includes('</svg>');
      }
    } catch (error) {
      console.error('Error validating SVG content:', error);
      return false;
    }
  }

  removeLogo(logoType: (typeof this.LOGO_TYPES)[number] = 'logo') {
    // Use null as an explicit deletion sentinel so that:
    // - UI hides the logo immediately
    // - updateSettings() sends { logo: null }
    this._internalBrandingSettings[logoType] = null;
    this.updateSettings();
    this._notificationService.showSuccessNotification(`${this.LOGO_LABELS[logoType]} has been successfully removed`);
  }

  onColorChange(colorType: keyof NonNullable<BrandingSettings['themeColors']>, value: string) {
    if (!this._internalBrandingSettings.themeColors) {
      this._internalBrandingSettings.themeColors = {};
    }
    (this._internalBrandingSettings.themeColors as any)[colorType] = value;
    this.debouncedUpdateSettings();
  }

  onColorPickerChange(colorType: keyof NonNullable<BrandingSettings['themeColors']>, value: string | object) {
    let colorValue = '';
    if (typeof value === 'string') {
      colorValue = value;
    } else if (value && typeof value === 'object' && 'hex' in value) {
      colorValue = (value as any).hex;
    }
    this.onColorChange(colorType, colorValue);
  }

  resetToDefaults() {
    this._internalBrandingSettings.themeColors = {
      primaryColor: '#FDE047',
    };
    this.updateSettings();
  }

  getColorValue(colorType: keyof NonNullable<BrandingSettings['themeColors']>): string {
    const currentColor = this._internalBrandingSettings.themeColors?.[colorType];
    const existingColor = this.currentBrandingSettings.themeColors?.[colorType];
    return currentColor || existingColor || this.getDefaultColor(colorType);
  }

  getColorKeys(): (keyof NonNullable<BrandingSettings['themeColors']>)[] {
    return Object.keys(this.colorLabels) as (keyof NonNullable<BrandingSettings['themeColors']>)[];
  }

  private getDefaultColor(colorType: keyof NonNullable<BrandingSettings['themeColors']>): string {
    const defaults: BrandingSettings['themeColors'] = {
      primaryColor: '#FDE047',
    };
    return defaults[colorType] || '#FDE047';
  }

  getLogoDisplayUrl(logoType: (typeof this.LOGO_TYPES)[number] = 'logo'): string | null {
    const currentLogo = this._internalBrandingSettings?.[logoType];
    
    // If user explicitly removed the logo (null), don't show anything
    if (currentLogo === null) return null;
    
    // Use current logo if set, otherwise fall back to existing logo
    const logo = currentLogo || this.currentBrandingSettings?.[logoType];
    if (!logo) return null;
    
    // Handle different logo formats
    if (logo.startsWith('data:') || logo.startsWith('http')) {
      return logo;
    }
    
    // Assume it's base64 and add data URL prefix
    return `data:image/svg+xml;base64,${logo}`;
  }

  private setLogoUploadingState(logoType: (typeof this.LOGO_TYPES)[number], value: boolean) {
    if (logoType === 'logo') {
      this.isUploading = value;
    } else {
      this.isSmallLogoUploading = value;
    }
  }

  private debouncedUpdateSettings() {
    // Clear existing timer
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
    
    // Show saving indicator
    this.isSaving = true;
    
    // Set new timer
    this._debounceTimer = setTimeout(() => {
      this.updateSettings();
    }, this.DEBOUNCE_DELAY);
  }

  private updateSettings() {
    const settings: BrandingSettings = {};
    
    // Handle logo
    if ('logo' in this._internalBrandingSettings) {
      settings.logo = this._internalBrandingSettings.logo;
    } else if (this.currentBrandingSettings.logo) {
      settings.logo = this.currentBrandingSettings.logo;
    }

    // Handle small logo
    if ('smallLogo' in this._internalBrandingSettings) {
      settings.smallLogo = this._internalBrandingSettings.smallLogo;
    } else if (this.currentBrandingSettings.smallLogo) {
      settings.smallLogo = this.currentBrandingSettings.smallLogo;
    }
    
    // Handle primary color
    if (this._internalBrandingSettings.themeColors?.primaryColor) {
      settings.themeColors = { primaryColor: this._internalBrandingSettings.themeColors.primaryColor };
    } else if (this.currentBrandingSettings.themeColors?.primaryColor) {
      settings.themeColors = { primaryColor: this.currentBrandingSettings.themeColors.primaryColor };
    }
    
    this.settingsUpdated.emit(settings);
    
    setTimeout(() => {
      this.isSaving = false;
    }, 200);
  }

  ngOnDestroy() {
    // Clear any pending debounced calls
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }
}
